"use client";

import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import type { Area, Point } from "react-easy-crop";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { Button, SubmitButton } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { cropImage } from "@/lib/image/crop-image";
import { Icon } from "@/components/ui/icon";

interface AvatarCropDialogProps {
  open: boolean;
  imageSrc: string | null;
  onCrop: (blob: Blob) => void;
  onCancel: () => void;
  cropShape?: "round" | "rect";
  /**
   * Crop box aspect ratio (width / height). Defaults to 1 (square) to preserve
   * existing avatar/org-logo behavior. Pass a wider value (e.g. 4) for full,
   * non-squared assets like email-signature logos.
   */
  aspect?: number;
  /**
   * Width in px of the image handed to `onCrop`. The height is derived from
   * `aspect`, so a non-square `aspect` is no longer squashed into a square
   * canvas. Defaults to 400 — what every caller got before spec 10.
   */
  outputWidth?: number;
  /**
   * Encoding of the blob handed to `onCrop`. Defaults to JPEG; pass PNG when the
   * crop must keep transparency (e.g. a logo drawn over a styled background).
   */
  outputType?: "image/jpeg" | "image/png";
  title?: string;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.01;

export function AvatarCropDialog({
  open,
  imageSrc,
  onCrop,
  onCancel,
  cropShape = "round",
  aspect = 1,
  outputWidth = 400,
  outputType = "image/jpeg",
  title = "Crop Photo",
}: AvatarCropDialogProps) {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleApply = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setIsProcessing(true);
    try {
      const blob = await cropImage(
        imageSrc,
        croppedAreaPixels,
        outputWidth,
        Math.round(outputWidth / aspect),
        outputType,
      );
      onCrop(blob);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      onCancel();
      setCrop({ x: 0, y: 0 });
      setZoom(MIN_ZOOM);
      setCroppedAreaPixels(null);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleClose();
      }}
      dismissible={!isProcessing}
    >
      <ModalContent size="sm">
        <ModalHeader title={title} iconName="IconCamera1" />

        <ModalBody className="flex flex-col gap-4">
          {/* `aspectRatio: "1"` is what `aspect-square` computed, so a square
              dialog is pixel-identical; a 16:9 crop now uses the dialog's full
              width instead of being letterboxed inside a square. */}
          <div
            data-testid="crop-preview"
            className="relative overflow-hidden rounded-xl bg-black/40"
            style={{ aspectRatio: String(aspect) }}
          >
            {imageSrc && (
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                cropShape={cropShape}
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                style={{
                  containerStyle: { borderRadius: 12 },
                  cropAreaStyle: {
                    border: "2px solid var(--border-alpha)",
                    boxShadow: "none",
                  },
                }}
                classes={{ containerClassName: "!bg-black/40" }}
              />
            )}
          </div>

          <div className="flex items-center gap-3 px-1">
            <button
              type="button"
              onClick={() => setZoom((z) => Math.max(MIN_ZOOM, z - 0.1))}
              className="shrink-0 size-7 rounded-md flex items-center justify-center hover:bg-contrast/6 transition-colors cursor-pointer"
            >
              <Icon
                name="IconMagnifyingGlass"
                size={13}
                className="text-heading opacity-40"
              />
            </button>
            <Slider
              value={[zoom]}
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={ZOOM_STEP}
              onValueChange={([v]) => setZoom(v)}
              className="flex-1"
            />
            <button
              type="button"
              onClick={() => setZoom((z) => Math.min(MAX_ZOOM, z + 0.1))}
              className="shrink-0 size-7 rounded-md flex items-center justify-center hover:bg-contrast/6 transition-colors cursor-pointer"
            >
              <Icon
                name="IconMagnifyingGlass"
                size={18}
                className="text-heading opacity-40"
              />
            </button>
          </div>
        </ModalBody>

        <ModalFooter align="between">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleClose}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <SubmitButton
            type="button"
            variant="primary"
            size="sm"
            onClick={handleApply}
            disabled={isProcessing || !croppedAreaPixels}
            loading={isProcessing}
            loadingLabel="Applying"
          >
            Apply
          </SubmitButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
